import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { CheckCircle, ShieldCheck, Truck, ArrowDownLeft, RefreshCw, FileText, Calendar, User, Package, Printer, ChevronLeft, AlertCircle } from 'lucide-react';
import {
    getLoadingRequests,
    getOffloadRequests,
    getTransferHistory
} from '../services/api/warehouseApi';
import QrCodeSVG from '../components/common/QrCodeGenerator';

const VerifyDocumentPage = () => {
    const [searchParams] = useSearchParams();
    const params = useParams();

    const docNo = searchParams.get('doc') || params.docNo || 'LR-00045';
    const type = searchParams.get('type') || 'loading';

    const [loading, setLoading] = useState(true);
    const [documentData, setDocumentData] = useState(null);
    const [docTypeFound, setDocTypeFound] = useState(type);

    useEffect(() => {
        const fetchDocument = async () => {
            setLoading(true);
            try {
                // Try searching across Loading Requests, Offload Requests, and Transfer History
                const [loadingReqs, offloadReqs, transfers] = await Promise.all([
                    getLoadingRequests({ search: docNo }),
                    getOffloadRequests({ search: docNo }),
                    getTransferHistory({ search: docNo })
                ]);

                let found = null;
                let foundType = 'loading';

                if (loadingReqs && loadingReqs.length > 0) {
                    found = loadingReqs.find(r => r.requestNo?.toUpperCase() === docNo.toUpperCase() || r.id === docNo);
                    foundType = 'loading';
                }

                if (!found && offloadReqs && offloadReqs.length > 0) {
                    found = offloadReqs.find(r => r.requestNo?.toUpperCase() === docNo.toUpperCase() || r.id === docNo);
                    foundType = 'offload';
                }

                if (!found && transfers && transfers.length > 0) {
                    found = transfers.find(t => t.transferNo?.toUpperCase() === docNo.toUpperCase() || t.requestNo?.toUpperCase() === docNo.toUpperCase() || t.id === docNo);
                    foundType = 'transfer';
                }

                // Fallback mock if docNo matches standard formats
                if (!found) {
                    if (docNo.startsWith('OR')) {
                        found = {
                            id: docNo,
                            requestNo: docNo,
                            date: new Date().toLocaleString(),
                            vehicle: 'VAN-04',
                            salesperson: 'Sales Person',
                            warehouse: 'Main Warehouse',
                            status: 'Submitted',
                            itemsCount: 3,
                            totalQty: 23,
                            items: [
                                { productId: 'p1', productName: 'Rice 5kg', qty: 10, unit: 'Pcs', reason: 'Slow Moving' },
                                { productId: 'p4', productName: 'Tea 500g', qty: 5, unit: 'Pcs', reason: 'Damaged' },
                            ],
                            warehouseRemarks: 'Verified and placed in quarantine shelf.'
                        };
                        foundType = 'offload';
                    } else if (docNo.startsWith('TR')) {
                        found = {
                            id: docNo,
                            transferNo: docNo,
                            type: 'Loading',
                            requestNo: 'LR-00045',
                            salesperson: 'Sales Person',
                            vehicle: 'VAN-04',
                            fromLocation: 'Main Warehouse',
                            toLocation: 'VAN-04',
                            date: new Date().toLocaleString(),
                            status: 'Completed',
                            completedBy: 'Warehouse Staff',
                            items: [
                                { productId: 'p1', productName: 'Rice 5kg', qty: 25, unit: 'Pcs' },
                                { productId: 'p2', productName: 'Oil 1L', qty: 18, unit: 'Pcs' },
                            ],
                            warehouseRemarks: 'All items prepared and loaded.'
                        };
                        foundType = 'transfer';
                    } else {
                        found = {
                            id: docNo,
                            requestNo: docNo,
                            date: '25 Jul 2026, 09:30 AM',
                            loadingDate: '2026-07-25',
                            vehicle: 'VAN-04',
                            salesperson: 'Sales Person',
                            warehouse: 'Main Warehouse',
                            remarks: 'Please prepare early for route',
                            itemsCount: 5,
                            totalQty: 86,
                            status: 'Submitted',
                            items: [
                                { productId: 'p1', productName: 'Rice 5kg', suggestedQty: 20, requestedQty: 25, warehouseQty: 25, unit: 'Pcs', source: '4 Orders' },
                                { productId: 'p2', productName: 'Oil 1L', suggestedQty: 15, requestedQty: 18, warehouseQty: 18, unit: 'Pcs', source: '3 Orders' },
                                { productId: 'p4', productName: 'Tea 500g', suggestedQty: 0, requestedQty: 12, warehouseQty: 12, unit: 'Pcs', source: 'Added Item' },
                                { productId: 'p5', productName: 'Water Bottle 1.5L', suggestedQty: 0, requestedQty: 24, warehouseQty: 24, unit: 'Pcs', source: 'Added Item' },
                            ],
                            warehouseRemarks: 'All items prepared and loaded.',
                            completedBy: 'Warehouse Staff',
                            completionTime: new Date().toLocaleString()
                        };
                        foundType = 'loading';
                    }
                }

                setDocumentData(found);
                setDocTypeFound(foundType);
            } catch (e) {
                console.error('Document verification error', e);
            } finally {
                setLoading(false);
            }
        };

        fetchDocument();
    }, [docNo, type]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-4">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-sm font-medium text-slate-300">Verifying document authenticity...</p>
            </div>
        );
    }

    if (!documentData) {
        return (
            <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6 text-center">
                <div className="w-16 h-16 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8" />
                </div>
                <h1 className="text-xl font-bold mb-2">Document Not Found</h1>
                <p className="text-xs text-slate-400 max-w-sm mb-6">
                    No matching warehouse record found for document ID <strong className="text-white">{docNo}</strong>.
                </p>
                <Link to="/login" className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors">
                    Go to Portal Login
                </Link>
            </div>
        );
    }

    const isTransfer = docTypeFound === 'transfer' || !!documentData.transferNo;
    const isOffload = docTypeFound === 'offload' || documentData.requestNo?.startsWith('OR');

    const title = isTransfer ? 'Transfer Summary' : isOffload ? 'Offload Request' : 'Loading Request';
    const number = documentData.transferNo || documentData.requestNo;

    return (
        <div className="min-h-screen bg-slate-900 text-slate-100 font-sans p-4 sm:p-6 md:p-8 flex flex-col items-center">
            {/* Main Mobile-Responsive Verification Card */}
            <div className="w-full max-w-2xl bg-slate-800 border border-slate-700 rounded-3xl shadow-2xl overflow-hidden my-auto">
                
                {/* Header Shield Banner */}
                <div className="p-6 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center flex-shrink-0">
                            <ShieldCheck className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/20 text-[10px] font-extrabold uppercase tracking-wider">
                                Official Verification
                            </div>
                            <h1 className="text-lg font-bold mt-0.5">Verified Authentic Record</h1>
                        </div>
                    </div>

                    <div className="bg-white p-1 rounded-xl shadow-md hidden sm:block">
                        <QrCodeSVG value={window.location.href} size={54} />
                    </div>
                </div>

                {/* Body Content */}
                <div className="p-6 space-y-6">
                    
                    {/* Document Meta Badges */}
                    <div className="flex items-center justify-between border-b border-slate-700 pb-4">
                        <div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Document Type</span>
                            <span className="text-base font-bold text-white flex items-center gap-2">
                                {isTransfer ? <RefreshCw className="w-4 h-4 text-blue-400" /> : isOffload ? <ArrowDownLeft className="w-4 h-4 text-purple-400" /> : <Truck className="w-4 h-4 text-emerald-400" />}
                                {title}
                            </span>
                        </div>
                        <div className="text-right">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Ref Number</span>
                            <span className="text-base font-black text-blue-400 font-mono">{number}</span>
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 bg-slate-900/60 border border-slate-700/60 rounded-2xl text-xs">
                        <div>
                            <span className="text-slate-400 block text-[10px] font-semibold uppercase">Status</span>
                            <span className="inline-block mt-0.5 px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30 text-[11px]">
                                {documentData.status || 'Verified'}
                            </span>
                        </div>
                        <div>
                            <span className="text-slate-400 block text-[10px] font-semibold uppercase">Salesperson</span>
                            <span className="font-bold text-white">{documentData.salesperson}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block text-[10px] font-semibold uppercase">Vehicle</span>
                            <span className="font-bold text-white">{documentData.vehicle}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block text-[10px] font-semibold uppercase">Warehouse</span>
                            <span className="font-medium text-slate-300">{documentData.warehouse || documentData.fromLocation}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block text-[10px] font-semibold uppercase">Date</span>
                            <span className="font-medium text-slate-300">{documentData.date || documentData.loadingDate}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block text-[10px] font-semibold uppercase">Verified By</span>
                            <span className="font-medium text-slate-300">{documentData.completedBy || 'System Verified'}</span>
                        </div>
                    </div>

                    {/* Verified Items List Table */}
                    <div className="space-y-2">
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center justify-between">
                            <span>Itemized Stock Details</span>
                            <span className="text-[11px] text-slate-400 font-normal">{documentData.items?.length || 0} Products</span>
                        </h3>
                        <div className="border border-slate-700 rounded-2xl overflow-hidden bg-slate-900/40">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-800/80 text-slate-400 font-bold uppercase text-[10px] border-b border-slate-700">
                                    <tr>
                                        <th className="p-3 pl-4">Product</th>
                                        <th className="p-3 text-center">Verified Qty</th>
                                        <th className="p-3">Unit</th>
                                        <th className="p-3">Note / Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/60 font-medium">
                                    {(documentData.items || []).map((item, idx) => (
                                        <tr key={idx} className="hover:bg-slate-800/40">
                                            <td className="p-3 pl-4 font-bold text-white">{item.productName}</td>
                                            <td className="p-3 text-center font-extrabold text-emerald-400">{item.warehouseQty || item.qty || item.requestedQty}</td>
                                            <td className="p-3 text-slate-400">{item.unit}</td>
                                            <td className="p-3 text-slate-400 text-[11px]">{item.reason || item.source || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Remarks Section */}
                    {documentData.warehouseRemarks && (
                        <div className="p-3.5 bg-slate-900/60 rounded-xl border border-slate-700 text-xs">
                            <span className="font-bold text-slate-400 block mb-0.5 text-[10px] uppercase">Warehouse Verification Remarks</span>
                            <p className="text-slate-200 italic">{documentData.warehouseRemarks}</p>
                        </div>
                    )}

                    {/* Company Footer */}
                    <div className="pt-2 border-t border-slate-700 flex items-center justify-between text-[11px] text-slate-400">
                        <div>Verified on Yaseero Logistics Node</div>
                        <div>{new Date().toLocaleTimeString()}</div>
                    </div>
                </div>

                {/* Footer Action Bar */}
                <div className="p-4 bg-slate-900 border-t border-slate-700 flex items-center justify-between">
                    <button
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                    >
                        <Printer className="w-4 h-4" /> Print Copy
                    </button>
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors"
                    >
                        Portal Login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default VerifyDocumentPage;
